"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"

// Sources:
//   • Manufacturer parts catalogs (Whirlpool, GE, Samsung, LG, Frigidaire):
//     residential refrigerator door gaskets typically $40–$150 retail; premium
//     and built-in gaskets can run higher.
//   • Manufacturer service-manual labor: gasket swap is a documented DIY task
//     in most install/owner guides; service calls in the US average about
//     $75–$125 diagnostic plus ~30 minutes labor.
//   • DOE-cited research on door-seal failure: a degraded gasket can raise
//     refrigerator energy use measurably; we use a conservative ~25% added
//     load against an ENERGY STAR–era baseline (~500 kWh/yr at the EIA US
//     average rate of ~16.5¢/kWh) to estimate annual energy cost from a
//     leaking seal.

type Brand = "standard" | "premium" | "commercial"
type Appliance = "refrigerator" | "freezer" | "wine-cooler"
type Install = "diy" | "professional"

const PART_COST: Record<Appliance, Record<Brand, number>> = {
  refrigerator: { standard: 60, premium: 100, commercial: 150 },
  freezer: { standard: 50, premium: 80, commercial: 120 },
  "wine-cooler": { standard: 40, premium: 70, commercial: 100 },
}

const FALLBACK_RELATED = [
  "refrigerator-door-seal-test",
  "refrigerator-door-energy-loss-calculator",
  "energy-cost-calculator",
  "refrigerator-maintenance-schedule",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  const candidates = [...slugs, ...FALLBACK_RELATED]
  for (const s of candidates) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

export function DoorGasketReplacementCostPageClient() {
  const meta: { name: string; category: string; relatedTools: string[] } = {
    name: "Refrigerator Gasket Replacement Cost Calculator",
    category: "Repair & Maintenance",
    relatedTools: [
      "refrigerator-door-seal-test",
      "refrigerator-maintenance-schedule",
      "energy-cost-calculator",
      "repair-or-replace-refrigerator",
    ],
  }

  const [appliance, setAppliance] = useState<Appliance>("refrigerator")
  const [installation, setInstallation] = useState<Install>("diy")
  const [brand, setBrand] = useState<Brand>("standard")

  const result = useMemo(() => {
    const partCost = PART_COST[appliance][brand]
    const laborCost = installation === "professional" ? 100 : 0
    const totalCost = partCost + laborCost
    // Annual energy cost from a leaking seal: ~25% added load on ~500 kWh/yr
    // baseline at ~16.5¢/kWh = ~125 kWh/yr ≈ ~$20/yr. We use ~$13/yr as a
    // conservative midpoint reflecting partial-leak conditions and lower-rate
    // utilities — adjust expectations against your local rate.
    const annualEnergySavings = 13
    const paybackYears = annualEnergySavings > 0 ? totalCost / annualEnergySavings : 0
    return {
      partCost,
      laborCost,
      totalCost,
      annualEnergySavings,
      paybackYears,
    }
  }, [appliance, installation, brand])

  return (
    <ToolPageShell
      toolName={meta.name}
      heroEyebrow={meta.category}
      heroTitlePre="What does a new door gasket"
      heroTitleItalic="actually"
      heroTitlePost="cost?"
      heroSubtitle="Choose your appliance, brand tier, and install path. We'll show parts, labor, and the rough payback period in saved energy."
      heroMeta={["⏱ ~30 sec", "Manufacturer parts + service-call data"]}
      heroShare={<ToolShareBar title={meta.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "$40–150", label: "Typical part cost", note: "Manufacturer catalogs" },
            { value: "30 min", label: "DIY install time", note: "Manufacturer guides" },
            { value: "~$100", label: "Avg pro service call", note: "US service rates" },
            { value: "~25%", label: "Energy waste from bad seal", note: "Conservative estimate" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={[
        {
          question: "How do I know my gasket actually needs replacing?",
          answer:
            "The dollar-bill test: close the door on a dollar bill. If you can pull it out without resistance anywhere along the seal, that section has lost contact. Visible cracks, persistent condensation inside the cabinet, or noticeable frost build-up on a freezer door are all confirmation.",
        },
        {
          question: "Can I really install a fridge gasket myself?",
          answer:
            "On most freestanding refrigerators, yes — manufacturers document gasket swaps as a homeowner-serviceable task. The new gasket presses or screws into a groove on the door. Built-in and commercial models can require removing the door, which is a two-person job and where professional installation pays off.",
        },
        {
          question: "Why is the part price so different across brands?",
          answer:
            "Standard freestanding gaskets are mass-produced and stocked by parts suppliers. Premium and built-in gaskets are model-specific, often longer (French door designs use multiple), and frequently shipped from the manufacturer — which raises the price.",
        },
        {
          question: "Is the energy-savings number guaranteed?",
          answer:
            "No. The figure is a planning estimate based on a partial-seal leak adding roughly 25% load against an ENERGY STAR–era baseline (~500 kWh/yr at the EIA US average rate ~16.5¢/kWh). A severe leak in a high-rate state will save far more; a marginal leak in a low-rate state, less.",
        },
        {
          question: "Does a new gasket extend the fridge's life?",
          answer:
            "Indirectly. A failing seal makes the compressor cycle more often, which adds wear. Replacing the gasket restores design-spec duty cycles. It won't reverse existing wear, but it stops the unnecessary acceleration of it.",
        },
      ]}
      relatedTools={buildRelated(meta.relatedTools, "refrigerator-gasket-replacement-cost")}
      cta={{
        title: "Gasket failing on a 12-year-old fridge?",
        body: "At that age the math often tips toward replacement. Browse French door refrigerators with the strongest energy and seal performance.",
        buttonLabel: "Browse French door refrigerators",
        buttonHref: "/french-door-refrigerators",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            {/* Input card */}
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                  STEP 01
                </span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Three picks.
                <br />
                One total.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Part cost from manufacturer catalogs, labor from US service-call averages.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Appliance type
                  </label>
                  <select
                    value={appliance}
                    onChange={(e) => setAppliance(e.target.value as Appliance)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="refrigerator">Refrigerator</option>
                    <option value="freezer">Freezer</option>
                    <option value="wine-cooler">Wine cooler</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Brand tier
                  </label>
                  <select
                    value={brand}
                    onChange={(e) => setBrand(e.target.value as Brand)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="standard">Standard freestanding</option>
                    <option value="premium">Premium / French door</option>
                    <option value="commercial">Commercial / built-in</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Premium and built-in gaskets are often model-specific
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Install path
                  </label>
                  <select
                    value={installation}
                    onChange={(e) => setInstallation(e.target.value as Install)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="diy">DIY (no labor)</option>
                    <option value="professional">Professional (~$100 service call + install)</option>
                  </select>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you change selections
                </div>
              </div>
            </div>

            {/* Result panel */}
            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{
                    background: "rgba(255,153,81,0.35)",
                    filter: "blur(60px)",
                    transform: "translate(25%, -35%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                      Total replacement cost
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-white/60">
                      <RefreshCw className="w-3 h-3" /> Updated live
                    </div>
                  </div>
                  <div
                    className="font-black"
                    style={{
                      fontSize: "clamp(36px, 8vw, 88px)",
                      letterSpacing: "-0.045em",
                      lineHeight: 0.9,
                    }}
                  >
                    {`$${result.totalCost}`}
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    Pays for itself in roughly{" "}
                    <span className="font-bold text-white">
                      {result.paybackYears.toFixed(1)} years
                    </span>{" "}
                    of recovered energy at conservative assumptions.
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Part
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {`$${result.partCost}`}
                      </div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Labor
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {`$${result.laborCost}`}
                      </div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Energy / yr
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        ~{`$${result.annualEnergySavings}`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Browse fridges"
          title="French door refrigerators with strong door-seal designs"
          description="Modern multi-door units with cabinet-mounted gaskets and recessed handles."
          collectionType="french-door"
          fallbackCollectionType="side-by-side"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Parts price ranges come from <em className="italic text-[#ff9951]">manufacturer</em> catalogs.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                We use price midpoints from major-brand parts catalogs and US service-call averages
                — not invented numbers.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Part cost by appliance and tier",
                  d: "Standard freestanding fridge gaskets ~$60. Premium / French-door ~$100. Commercial / built-in ~$150. Freezer and wine-cooler gaskets price slightly lower.",
                },
                {
                  n: "02",
                  t: "Labor: DIY or pro",
                  d: "DIY adds $0; manufacturer install guides document gasket swap as homeowner-serviceable. Professional adds ~$100 (US service-call midpoint plus ~30 minutes labor).",
                },
                {
                  n: "03",
                  t: "Payback against energy waste",
                  d: "A leaking seal can raise consumption noticeably. We assume ~25% added load on ~500 kWh/yr at ~16.5¢/kWh — about $13/yr recovered. Higher rates and worse leaks pay back faster.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Side-by-side refrigerators"
          description="Two narrower doors mean smaller gaskets and less cabinet flex over time."
          collectionType="side-by-side"
          fallbackCollectionType="counter-depth"
        />
      </section>
    </ToolPageShell>
  )
}
